const SPINNER = '<div class="flex justify-center py-10"><span class="loading loading-spinner loading-sm"></span></div>'

// Primes a modal's deferred turbo-frames just before its <dialog> opens. Shared by
// modal_controller (click triggers) and shortcuts_controller (the `c` key), which
// both have to do this and previously each carried their own copy.
//
// FD-98/FD-501: a closed daisyUI .modal is laid out (visibility:hidden, not
// display:none), so a loading:lazy frame would auto-load on render — and for the
// detail modal would duplicate _detail_body's inline forms. The frames instead carry
// data-modal-src and are fetched here, at open time, exactly once.
//
// A frame marked data-modal-dynamic renders per-record or per-group content — FD-568's
// delete confirmation, CLM-442's create form — so it re-fetches on every open from the
// `src` the trigger supplied. Returns that frame (or null) so the caller can wait on it.
export function primeModalFrames(dialog, src = null) {
  dialog.querySelectorAll("turbo-frame[data-modal-src]:not([src]):not([data-modal-dynamic])").forEach((frame) => {
    frame.setAttribute("src", frame.dataset.modalSrc)
  })

  const frame = dialog.querySelector("turbo-frame[data-modal-dynamic]")
  // CLM-442: triggers that pass no src of their own — the toolbar/sidebar buttons and
  // the `c` shortcut — fall back to the frame's data-modal-src, so a plain "Add work
  // item" always opens a clean form instead of the last group's prefills.
  const wanted = src || frame?.dataset?.modalSrc
  if (!frame || !wanted) return null

  // Reset the way Turbo's own frame reload does (drop `complete`, null the src, set it
  // again). Nulling first also cancels an in-flight fetch, and makes the re-fetch
  // independent of the fact that Turbo rewrites `src` to the absolute response URL
  // after a load (which is what makes re-setting the relative path register as a change).
  frame.innerHTML = SPINNER
  frame.removeAttribute("complete")
  frame.removeAttribute("src")
  frame.setAttribute("src", wanted)
  return frame
}

// FD-591: restore a dialog's STATIC deferred frames to their unloaded state when it
// closes. The Drafts page is the first surface to render one detail modal per row, so
// opening several editors in one page session would otherwise leave every _detail_body
// loaded at once — duplicate un-scoped ids (#work_item_title, [data-shortcut-item]) that
// the global shortcuts_controller reads with document.querySelector, hijacking copy/focus
// shortcuts to the wrong item. Dropping a closed dialog's body back to the placeholder
// keeps at most one _detail_body in the DOM; the static frame simply re-fetches on the
// next open (same as its first open). Dynamic frames (data-modal-dynamic) already reset
// on open, so they're left alone. Mirrors primeModalFrames' own reset sequence.
export function resetModalFrames(dialog) {
  staticFrames(dialog).forEach(unloadFrame)
}

// FD-603: re-fetch an OPEN dialog's static frames in place. A write made from inside the
// detail modal PATCHes and redirect_back's, which Turbo turns into a page-refresh morph —
// and modal_controller's own preserveOpenDialog guard makes idiomorph skip the open dialog
// and its whole subtree, so the modal keeps rendering the pre-save values (reported as
// "changing state from a draft row still shows the old state"). The dialog can't be morphed
// without stranding it in the top layer, so re-fetch its body instead.
//
// Turbo's own FrameElement#reload, NOT the unload-then-set-src above: reload keeps the
// current body on screen until the response lands. Swapping in the spinner first collapses
// the modal-box to ~60px and then springs it back, which is not just a flash — it moves the
// header's Close button out from under a click that was already on its way, so closing the
// modal right after an edit silently does nothing.
//
// Dynamic frames re-fetch on their own next open and are left alone, as in resetModalFrames.
export function refreshModalFrames(dialog) {
  staticFrames(dialog).forEach((frame) => {
    if (frame.src) frame.reload()
  })
}

function staticFrames(dialog) {
  return dialog.querySelectorAll("turbo-frame[data-modal-src]:not([data-modal-dynamic])")
}

// Turbo's own frame-reload sequence: drop `complete`, null the src (cancelling any
// in-flight fetch), leaving the frame ready to load again.
function unloadFrame(frame) {
  frame.innerHTML = SPINNER
  frame.removeAttribute("complete")
  frame.removeAttribute("src")
}
